import { useState, useEffect } from "react";
import { GraduationCap, Sparkles, X, Trophy, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const CreditEarnedModal = ({ isOpen, onClose, creditsEarned, totalCredits, contentTitle }) => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.5, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Confetti Effect */}
          {showConfetti && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ 
                    y: -20, 
                    x: Math.random() * 300, 
                    rotate: 0,
                    opacity: 1 
                  }}
                  animate={{ 
                    y: 400, 
                    rotate: Math.random() * 360,
                    opacity: 0 
                  }}
                  transition={{ 
                    duration: 2 + Math.random(), 
                    delay: Math.random() * 0.5,
                    ease: "easeOut"
                  }}
                  className={`absolute w-3 h-3 rounded-full ${
                    ['bg-yellow-400', 'bg-pink-400', 'bg-blue-400', 'bg-green-400', 'bg-purple-400'][i % 5]
                  }`}
                />
              ))}
            </div>
          )}

          {/* Header */}
          <div className="bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-400 p-8 text-center relative">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
            >
              <Trophy className="w-10 h-10 text-amber-500" />
            </motion.div>

            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-2xl font-bold text-white mb-2"
            >
              축하합니다! 🎉
            </motion.h2>

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.4 }}
              className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full"
            >
              <GraduationCap className="w-5 h-5 text-white" />
              <span className="text-white font-bold text-xl">+{creditsEarned}학점</span>
            </motion.div>
          </div>

          {/* Body */}
          <div className="p-6 text-center">
            <p className="text-stone-600 mb-4 line-clamp-2">
              <span className="font-medium text-stone-800">"{contentTitle}"</span>
              <br />
              콘텐츠를 완료했습니다!
            </p>

            <div className="bg-gradient-to-r from-burgundy-50 to-pink-50 rounded-2xl p-5 mb-6">
              <p className="text-sm text-stone-500 mb-2">현재 이수학점</p>
              <div className="flex items-center justify-center gap-2">
                <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
                <span className="text-4xl font-bold text-burgundy-600">{totalCredits}</span>
                <span className="text-xl text-stone-500">학점</span>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-full py-3 bg-gradient-to-r from-burgundy-500 to-burgundy-600 text-white rounded-xl font-medium hover:from-burgundy-600 hover:to-burgundy-700 transition-all"
            >
              계속 학습하기
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreditEarnedModal;
